'use client';

import { Monitor, Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/hooks/use-theme';
import { cn } from '@/lib/utils';

type ThemeToggleProps = {
  /**
   * Size of the button in pixels (default: 24)
   */
  size?: number;
  /**
   * Size of the icon in pixels (default: size * 0.75)
   */
  iconSize?: number;
  /**
   * Additional class names to apply
   */
  className?: string;
};

export const ThemeToggle = ({
  size = 24,
  iconSize,
  className,
}: ThemeToggleProps) => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const resolvedIconSize = iconSize ?? size * 0.75;

  if (!mounted) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'group relative size-8 p-0 hover:cursor-pointer',
          'hover:bg-accent hover:scale-105 active:scale-100',
          'transition-transform duration-200',
          'focus-visible:ring-ring focus-visible:ring-1 focus-visible:ring-offset-1',
          className
        )}
        style={{ width: size, height: size }}
      >
        <Sun
          style={{ width: resolvedIconSize, height: resolvedIconSize }}
          className="text-muted-foreground group-hover:text-white"
        />
        <span className="sr-only">Toggle theme</span>
      </Button>
    );
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'group relative size-8 p-0 hover:cursor-pointer',
            'hover:bg-accent hover:scale-105 active:scale-100',
            'transition-transform duration-200',
            'focus-visible:ring-ring focus-visible:ring-1 focus-visible:ring-offset-1',
            className
          )}
          style={{ width: size, height: size }}
        >
          {/* Light Sun Icon */}
          <Sun
            style={{ width: resolvedIconSize, height: resolvedIconSize }}
            className="text-muted-foreground visible absolute z-10 scale-100 rotate-0 transition-transform duration-500 ease-in-out group-hover:text-white dark:invisible dark:scale-0 dark:-rotate-90"
          />
          {/* Dark Moon Icon */}
          <Moon
            style={{ width: resolvedIconSize, height: resolvedIconSize }}
            className="text-muted-foreground invisible absolute z-20 scale-0 rotate-90 transition-transform duration-500 ease-in-out group-hover:text-white dark:visible dark:scale-100 dark:rotate-0"
          />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" forceMount>
        <DropdownMenuItem
          onClick={() => setTheme('light')}
          className={cn('cursor-pointer', theme === 'light' && 'bg-accent')}
        >
          <Sun className="mr-2 h-4 w-4" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme('dark')}
          className={cn('cursor-pointer', theme === 'dark' && 'bg-accent')}
        >
          <Moon className="mr-2 h-4 w-4" />
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme('system')}
          className={cn('cursor-pointer', theme === 'system' && 'bg-accent')}
        >
          <Monitor className="mr-2 h-4 w-4" />
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
